import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import BrandMark from "@/components/BrandMark";

const HERO_IMG = "https://images.pexels.com/photos/18112392/pexels-photo-18112392.jpeg";
const INTENSITY_IMG = "https://images.pexels.com/photos/9602280/pexels-photo-9602280.jpeg";

const services = [
  { code: "01", title: "Custom Meal Plans", body: "Macro-tuned nutrition built around your goals, lifestyle and preferences." },
  { code: "02", title: "Workout Programming", body: "Periodized training for muscle, fat loss, strength or event prep. Logged & tracked." },
  { code: "03", title: "Weekly Check-ins", body: "Photos, metrics, mood, sleep. We adapt your plan every 7 days." },
  { code: "04", title: "Cardio Regimens", body: "Steady-state, HIIT, step targets — dialed to your conditioning and recovery." },
  { code: "05", title: "Supplement Guidance", body: "What you actually need. Nothing you don't. Sourced and timed properly." },
  { code: "06", title: "Bloodwork Reviews", body: "Annotated panels. Health markers. Optimization without compromise." },
  { code: "07", title: "PED Protocols", body: "Evidence-based, dosed, monitored. Bloodwork-informed adjustments (advanced clients)." },
  { code: "08", title: "Video Form Review", body: "Frame-by-frame critique. Cue corrections. Injury prevention." },
];

const clientTypes = [
  { tag: "Bodybuilders", desc: "Off-season mass, prep, peak week." },
  { tag: "Lifestyle & Wellness", desc: "Look better, feel better, build a sustainable routine." },
  { tag: "Fat Loss", desc: "Cut without crashing — protect muscle, energy, mood." },
  { tag: "Muscle Building", desc: "Structured hypertrophy for real, lasting size." },
  { tag: "Event Prep", desc: "Weddings, vacations, photoshoots — peaked on the day." },
  { tag: "First-Timers", desc: "New to lifting? I'll meet you where you are." },
];

export default function Landing() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Inquiry sent. I'll reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Could not send inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] text-white min-h-screen">
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <BrandMark data-testid="landing-logo" />
          <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.25em] text-zinc-400">
            <a href="#services" className="hover:text-white transition">Services</a>
            <a href="#who" className="hover:text-white transition">Who It's For</a>
            <a href="#pricing" className="hover:text-white transition">Pricing</a>
            <a href="#process" className="hover:text-white transition">Process</a>
            <a href="#contact" className="hover:text-white transition">Contact</a>
          </nav>
          <Link to="/login">
            <Button data-testid="header-login-button" className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white text-xs uppercase tracking-[0.2em] h-9 px-4">
              Client Login
            </Button>
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
          <div className="text-[10px] tracking-[0.5em] text-[#FF3B30] mb-6 font-bold uppercase">Meet Me at the Bar</div>
          <h1 className="font-display text-6xl sm:text-8xl lg:text-9xl uppercase leading-[0.85] max-w-5xl" data-testid="hero-headline">
            Training and diet plans<br/>that fit <span className="text-[#FF3B30]">your goals.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-zinc-300 text-lg leading-relaxed">
            Build muscle. Drop fat. Get stage-ready or wedding-ready. Whether you're a competing bodybuilder, a wellness client, or just want to look and feel your best — I build the plan around <em className="not-italic text-white">you</em>.
          </p>
          <div className="mt-10 flex gap-3 flex-wrap">
            <a href="#contact">
              <Button data-testid="hero-cta-apply" className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-12 px-8 text-xs">Start Coaching</Button>
            </a>
            <a href="#services">
              <Button variant="outline" data-testid="hero-cta-services" className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-white hover:text-black uppercase tracking-[0.2em] h-12 px-8 text-xs">What I Offer</Button>
            </a>
          </div>
        </div>
        <div className="relative border-t border-zinc-800 grid grid-cols-2 md:grid-cols-4">
          {[["1:1", "ATTENTION"], ["24/7", "DIRECT ACCESS"], ["7-DAY", "CHECK-INS"], ["100%", "CUSTOMIZED"]].map(([k, v]) => (
            <div key={k} className="border-r border-zinc-800 last:border-r-0 px-6 py-8">
              <div className="font-display text-3xl text-white">{k}</div>
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 mt-1 uppercase">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section id="who" className="py-24 md:py-32 px-6 md:px-10 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-[#FF3B30] uppercase mb-3">/ Who it's for</div>
            <h2 className="font-display text-5xl sm:text-6xl uppercase max-w-3xl leading-none">Every goal. Every level.</h2>
          </div>
          <p className="max-w-sm text-zinc-400">Coaching that adapts to your life — not the other way around.</p>
        </div>
        <div className="border-l border-t border-zinc-800 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {clientTypes.map((c, i) => (
            <div key={c.tag} className="grid-cell" data-testid={`audience-${i}`}>
              <div className="font-mono text-xs text-[#FF3B30] mb-4">0{i + 1}</div>
              <h3 className="font-display text-3xl uppercase mb-2">{c.tag}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 md:py-32 px-6 md:px-10 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-[#FF3B30] uppercase mb-3">/ Services</div>
            <h2 className="font-display text-5xl sm:text-6xl uppercase max-w-2xl leading-none">Everything you need. Nothing you don't.</h2>
          </div>
          <p className="max-w-sm text-zinc-400">A complete coaching system — built around your physique, recovery and lifestyle.</p>
        </div>
        <div className="border-l border-t border-zinc-800 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <div key={s.code} className="grid-cell" data-testid={`service-${s.code}`}>
              <div className="font-mono text-xs text-[#FF3B30] mb-6">{s.code}</div>
              <h3 className="font-display text-2xl uppercase mb-3">{s.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32 px-6 md:px-10 max-w-7xl mx-auto">
        <PricingSection />
      </section>

      {/* PROCESS */}
      <section id="process" className="relative py-24 md:py-32">
        <div className="absolute inset-0">
          <img src={INTENSITY_IMG} alt="" className="w-full h-full object-cover opacity-30 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[10px] tracking-[0.4em] text-[#FF3B30] uppercase mb-3">/ Process</div>
          <h2 className="font-display text-5xl sm:text-6xl uppercase max-w-3xl leading-none mb-16">The system that turns work into results.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800">
            {[
              ["01", "Onboarding", "Goals, history, lifestyle, photos. Baselines logged. Plan built around YOU."],
              ["02", "Weekly Check-ins", "Submit photos & metrics. I review. Plan adapts within 48 hours."],
              ["03", "Continuous Access", "Question, form review, off-day call — I'm one message away."],
            ].map(([k, t, b]) => (
              <div key={k} className="bg-[#0A0A0A] p-10">
                <div className="font-mono text-[#FF3B30] text-xs mb-6">STEP {k}</div>
                <h3 className="font-display text-3xl uppercase mb-4">{t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-32 px-6 md:px-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
          {/* Form */}
          <div className="bg-[#0A0A0A] p-10 md:p-14">
            <div className="text-[10px] tracking-[0.4em] text-[#FF3B30] uppercase mb-3">/ Start Coaching</div>
            <h2 className="font-display text-5xl uppercase leading-none mb-3">Inquire</h2>
            <p className="text-zinc-400 mb-10 text-sm">Tell me about yourself and your goal. I respond personally within 24 hours.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Name</label>
                <Input data-testid="inquiry-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-12" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Email</label>
                  <Input data-testid="inquiry-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-12" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Phone</label>
                  <Input data-testid="inquiry-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-12" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Your goal</label>
                <Textarea data-testid="inquiry-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required placeholder="Build muscle, lose fat, prep for a wedding, compete in a show..." className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="inquiry-submit" className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-12 px-8 text-xs w-full">
                {submitting ? "Sending..." : "Send Inquiry"}
              </Button>
            </form>
          </div>
          {/* Contact details */}
          <div className="bg-[#121212] p-10 md:p-14 flex flex-col">
            <div className="text-[10px] tracking-[0.4em] text-[#FF3B30] uppercase mb-3">/ Contact</div>
            <h2 className="font-display text-5xl uppercase leading-none mb-10">Get in touch</h2>
            <div className="space-y-8 flex-1">
              <ContactRow label="Email" value="allensbodybuilding@gmail.com" href="mailto:allensbodybuilding@gmail.com" testid="contact-email" />
              <ContactRow label="Phone" value="(832) 693-1539" href="tel:+18326931539" testid="contact-phone" />
              <ContactRow label="Instagram" value="@douglasrallen6" href="https://instagram.com/douglasrallen6" testid="contact-instagram" />
              <ContactRow label="TikTok" value="@douglasallen037" href="https://tiktok.com/@douglasallen037" testid="contact-tiktok" />
            </div>
            <div className="mt-10 pt-10 border-t border-zinc-800 text-zinc-500 text-xs uppercase tracking-[0.25em]">
              Replies within 24 hours · Worldwide online coaching
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-zinc-800 py-10 px-6 md:px-10">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4 text-xs uppercase tracking-[0.25em] text-zinc-500">
          <BrandMark size="sm" />
          <div className="hidden sm:block">Meet Me at the Bar · © 2020 Allen's Bodybuilding</div>
        </div>
      </footer>
    </div>
  );
}

function ContactRow({ label, value, href, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className="block group">
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">{label}</div>
      <div className="font-display text-3xl tracking-wide text-white group-hover:text-[#FF3B30] transition break-all">{value}</div>
    </a>
  );
}

function PricingSection() {
  const [tiers, setTiers] = useState([]);
  const [category, setCategory] = useState("standard");

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setTiers(data.tiers)).catch(() => {});
  }, []);

  const mailtoFor = (tier) => {
    const subject = encodeURIComponent(`Coaching inquiry — ${tier.name}`);
    const body = encodeURIComponent(
      `Hi Allen,\n\nI'd like to start with the ${tier.name} ($${Math.round(tier.amount)}).\n\nA bit about me and my goals:\n\n`
    );
    return `mailto:allensbodybuilding@gmail.com?subject=${subject}&body=${body}`;
  };

  const filtered = tiers.filter((t) => t.category === category);
  const moneyFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const perMoFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  return (
    <div>
      <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-[#FF3B30] uppercase mb-3">/ Pricing</div>
          <h2 className="font-display text-5xl sm:text-6xl uppercase leading-none">Pick your plan.</h2>
          <p className="text-zinc-400 mt-4 max-w-xl text-sm">Commit longer, save more. All plans include the full coaching system — meal plans, training, check-ins, 24/7 access.</p>
        </div>
        <div className="flex gap-1 border border-zinc-800 p-1">
          {[["standard","Standard"],["student","Student / Military"]].map(([v, l]) => (
            <button key={v} onClick={() => setCategory(v)} data-testid={`pricing-tab-${v}`}
              className={`px-4 h-10 text-[10px] uppercase tracking-[0.25em] ${category === v ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
        {filtered.map((t) => {
          const isFeature = t.id === "12mo" || t.id === "student_12mo";
          return (
            <div key={t.id} data-testid={`pricing-card-${t.id}`}
              className={`p-6 flex flex-col ${isFeature ? "bg-[#1A0F0F] border-l-2 border-l-[#FF3B30]" : "bg-[#0A0A0A]"}`}>
              {isFeature && <div className="text-[9px] uppercase tracking-[0.4em] text-[#FF3B30] mb-2">Best Value</div>}
              <div className="font-display text-xl uppercase mb-1">{t.name}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-6">{t.note}</div>
              <div className="mb-2">
                <div className="font-display text-5xl">{moneyFmt.format(t.amount)}</div>
                {t.id !== "monthly" && (
                  <div className="text-xs text-zinc-500 mt-1">{perMoFmt.format(t.per_mo)} / mo equivalent</div>
                )}
              </div>
              <div className="mt-auto pt-6">
                <a href={mailtoFor(t)} data-testid={`pricing-buy-${t.id}`}
                   className="block">
                  <Button className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-11 w-full text-xs">
                    Start Now
                  </Button>
                </a>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-zinc-500 uppercase tracking-[0.25em] text-center mt-8">
        Click Start Now to email Allen directly · Personalized reply within 24 hours
      </div>
    </div>
  );
}
